import { CdkDragRelease, CdkDragStart } from "@angular/cdk/drag-drop";
import { Component, HostBinding, Input, OnDestroy, OnInit, QueryList, ViewChildren } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { VisionboardItem, VisionboardTemplateLane } from "../../models";
import {
  VisionboardItemCompatibilityService,
  VisionboardWorkspaceItemsStoreService,
  VisionboardWorkspaceTemplateService,
} from "../../services";
import { VisionboardWorkspaceTemplateLaneItemComponent } from "../visionboard-workspace-template-lane-item/visionboard-workspace-template-lane-item.component";

export interface IVisionboardWorkspaceTemplateLaneComponent {
  promptLaneItemSelection(): void;
}

@Component({
    selector: "app-visionboard-workspace-template-lane",
    templateUrl: "./visionboard-workspace-template-lane.component.html",
    styleUrls: ["./visionboard-workspace-template-lane.component.scss"],
    standalone: false
})
export class VisionboardWorkspaceTemplateLaneComponent implements OnInit, OnDestroy, IVisionboardWorkspaceTemplateLaneComponent {
  private _destroyed$: Subject<void> = new Subject<void>();

  @Input() lane: VisionboardTemplateLane;
  @Input() showTitle: boolean;
  @Input() showBorder: boolean;
  @Input() disableChecks: boolean;

  @HostBinding("class.disabled") disabled: boolean;
  infoText: string;

  @ViewChildren(VisionboardWorkspaceTemplateLaneItemComponent)
  laneItems: QueryList<VisionboardWorkspaceTemplateLaneItemComponent>;

  indices: number[];
  breakGrid: string;
  dragging: boolean;
  lastEnterIndex: number;

  constructor(
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardWorkspaceTemplateService: VisionboardWorkspaceTemplateService,
    private visionboardItemCompatibilityService: VisionboardItemCompatibilityService,
  ) {
    this.lastEnterIndex = null;
  }

  ngOnInit(): void {
    this.indices = this.lane.items.map((v, i, a) => i);

    this.setDisabled();

    if (this.lane.items.length > 8) {
      this.breakGrid = "breakGrid2";
    } else if (this.lane.items.length > 4) {
      this.breakGrid = "breakGrid1";
    } else {
      this.breakGrid = "";
    }

    // Allow item-count specific styling if necessary.
    this.breakGrid += ` breakGrid-${this.lane.items.length}-items`;

    this.visionboardWorkspaceTemplateService.laneItemsUpdated$.pipe(takeUntil(this._destroyed$)).subscribe(lane => {
      if (this.lane === lane) {
        this.setLaneItems();
      }

      this.setDisabled();
    });

    this.visionboardWorkspaceItemsStoreService.itemRemoved$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: visionboardItem => {
        this.laneItems.forEach(laneItemComponent => {
          if (laneItemComponent.item?.variantSkuId === visionboardItem.variantSkuId) {
            laneItemComponent.clearItem();
          }
        });
      },
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onMouseEnter(index: number): void {
    if (this.dragging) {
      this.lastEnterIndex = index;
    }
  }

  onMouseLeave(): void {
    if (this.dragging) {
      this.lastEnterIndex = null;
    }
  }

  onTouchMove(touch: TouchEvent): void {
    if (this.dragging) {
      const element = document
        .elementsFromPoint(touch.touches[0].clientX, touch.touches[0].clientY)
        .find(i => i.classList.contains("lane-item"));

      this.lastEnterIndex = element ? parseInt(element.id) : null;
    }
  }

  onItemChange(item: [number, VisionboardItem]): void {
    this.lane.items[item[0]] = item[1];
  }

  onDragStart(_: CdkDragStart, __: number): void {
    this.dragging = true;
  }

  onDragReleased(_: CdkDragRelease, index: number): void {
    this.dragging = false;

    if (this.lastEnterIndex !== null && this.lastEnterIndex >= 0) {
      const itemAtLastIndex = this.lane.items[this.lastEnterIndex];
      const itemDragged = this.lane.items[index];

      if (!itemAtLastIndex || !itemAtLastIndex.isVenueProvided) {
        this.lane.items[index] = itemAtLastIndex;
        this.lane.items[this.lastEnterIndex] = itemDragged;
        itemDragged.templateOrder = this.lastEnterIndex;

        if (itemAtLastIndex) {
          itemAtLastIndex.templateOrder = index;
        }

        this.setLaneItems();
        this.visionboardWorkspaceItemsStoreService.save();
      }
    }

    this.lastEnterIndex = null;
  }

  draggingDisabled(index: number): boolean {
    return !this.lane.items[index] || this.lane.items[index].isVenueProvided || this.lane.items[index].static;
  }

  promptLaneItemSelection(triggerOnboarding?: boolean): void {
    const laneItem = this.laneItems.find(i => !i.item);
    if (laneItem) {
      laneItem.promptItemSelection(triggerOnboarding);
    }
  }

  private setLaneItems(): void {
    this.laneItems.forEach(i => i.setItem());
  }

  private setDisabled(): void {
    const category = Object.keys(this.lane.categories)[0];
    const subCategory = category && this.lane.categories[category][0];

    this.disabled = this.lane.items.every(i => !i) && !this.visionboardItemCompatibilityService.canSelectChildItems(category, subCategory);

    this.infoText = this.visionboardItemCompatibilityService.getParentRequiredReason(category, subCategory);
  }
}
